/**
 * Call log and the quick-log form.
 *
 * The form is intentionally one screen with no candidate lookup step: the
 * spreadsheet's real advantage was that logging a call took five seconds, and
 * a system that takes thirty will simply not be used.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileSpreadsheet,
  Loader2,
  PhoneCall,
  Search,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import ImportPanel from '../components/ImportPanel';
import { client, errorMessage } from '../api/client';
import { downloadExport, exportErrorMessage } from '../lib/exportDownload';
import {
  canSeeAllCompanies,
  isCompanyAdmin,
  isSupervisor,
  useAuth,
} from '../store/auth';
import { sanitiseMobileInput } from '../lib/phone';
import DeleteRecordDialog from '../components/DeleteRecordDialog';
import RecruiterFilter from '../components/RecruiterFilter';
import { canBulkImportCalls, canLogCalls, seesOthersWork } from '../lib/teamView';
import { useDebounced } from '../lib/useDebounced';
import { placedWarningCopy } from '../lib/placedWarning';
import { titleCaseName, titleCaseNameOr } from '../lib/titleCase';
import { usePositiveOutcomes } from '../lib/usePositiveOutcomes';
import {
  OUTCOMES,
  OUTCOME_FILTER_OPTIONS,
  OUTCOME_LABELS,
} from '../lib/callOutcomes';
import CallsTable from '../components/CallsTable';
import { EditCallDialog, FollowUpDialog } from '../components/CallDialogs';
import ClientSelect from '../components/ClientSelect';
import type {
  Call,
  CallOutcome,
  CallSubmitResult,
  Page,
  PossibleDuplicates,
  PriorContact,
  PriorSubmissions,
  StillToCall,
} from '../types';

/**
 * Rows per page. The endpoint caps limit at 200; fifty is what this screen
 * has always asked for and the number the paging is built around.
 */
const PAGE_SIZE = 50;

/** Outcomes that create a followup, flagged so the recruiter knows. */
const COMMITMENT_OUTCOMES = new Set<CallOutcome>([
  'call_back',
  'interested',
  'hold',
  'info_sent',
  'awaiting_confirmation',
  'busy',
  'not_picking',
  'switched_off',
]);

/**
 * An Indian mobile, once the country code or trunk prefix is dropped.
 *
 * Mirrors parse_phone on the server, which is what actually decides the lookup
 * — this only decides when the field holds enough to be worth asking about, so
 * a partial number never sends a request.
 */
function mobileDigits(raw: string): string | null {
  const digits = raw.replace(/\D/g, '').slice(-10);
  return /^[6-9]\d{9}$/.test(digits) ? digits : null;
}

/** Whole days between a call date and today, for "3 days ago". */
function daysAgo(isoDate: string): number {
  const then = new Date(`${isoDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((today.getTime() - then.getTime()) / 86_400_000);
}

function whenPhrase(isoDate: string): string {
  const days = daysAgo(isoDate);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}


/**
 * Which client to submit against, when the call did not say.
 *
 * The Client field on the call form is optional and Submission.client_id is
 * not nullable, so this is the one thing that can be missing. One field: the
 * position, date, candidate and recruiter all come off the call, and letting
 * them be retyped here would let the submission disagree with the call it
 * came from.
 */
function TrackDialog({
  call,
  submitting,
  onCancel,
  onConfirm,
}: {
  call: Call;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (clientId: string) => void;
}) {
  const [clientId, setClientId] = useState('');

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card w-full max-w-sm space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!clientId) {
            toast.error('Choose a client');
            return;
          }
          onConfirm(clientId);
        }}
      >
        <h3 className="text-sm font-semibold text-gray-900">
          Submit {titleCaseName(call.candidateName) ?? 'this candidate'} to a client
        </h3>
        <p className="text-xs text-gray-500">
          This call has no client on it, so there is nothing to submit against
          yet. Everything else — the position
          {call.position ? ` (${call.position})` : ''} and the call date — comes
          from the call.
        </p>

        <label className="block text-xs text-gray-600">
          Client
          {/* Same set as the edit dialog: every client. This used to leave the
              imported ones out, on the grounds that a spreadsheet spelling was
              not an answer to "who was the work for" -- it was the same company
              under a typo, one of three rows. The duplicates have since been
              merged, so the imported rows that remain are the companies
              themselves, and excluding them stopped meaning "not a typo" and
              started meaning "not offered at all". */}
          <ClientSelect
            purpose="work"
            className="input mt-1"
            value={clientId}
            onChange={setClientId}
            placeholder="Choose a client…"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Correcting a call that was already logged.
 *
 * Sends only the fields that actually changed, which is not a nicety: the
 * server reads what was set rather than what was sent, so a form posting every
 * box it rendered would ask to change the outcome on every save -- tripping
 * the rank gate on a superseded call that the recruiter only wanted to add a
 * remark to, and putting a no-op into the audit log every time.
 *
 * Four of the five fields are ordinary. The two that are not say so on screen
 * rather than failing at the server:
 *
 * * **Outcome** is disabled once a later call exists, because only the most
 *   recent call feeds the Interested list, the still-to-call count and this
 *   report's own filter. The server enforces it; this explains it.
 * * **Candidate** re-points the call at the person it was really about -- the
 *   correction the "already on file under a different name" warning exists to
 *   prompt. Behind a deliberate extra click, because it is a move rather than
 *   an edit and can be refused by the server when a submission would be left
 *   behind.
 *
 * The candidate's mobile is not here. It lives on the Candidate as the
 * deduplication key, and the Candidates edit dialog locks it for that reason.
 */
export default function CallsPage() {
  const queryClient = useQueryClient();
  const me = useAuth((state) => state.user);
  /**
   * An owner has no call-back list, and the number they were shown was wrong.
   *
   * The card counts candidates whose most recent call was left open, against
   * whoever made that call. An owner does not make calls -- but they do run
   * imports, and every row whose Recruiter column matches no user is
   * attributed to the uploader by design. So the owner's "worklist" was the
   * unmatched half of every workbook anybody has ever uploaded: 35,032 people
   * they have never rung and will not be ringing.
   *
   * Worse with no company chosen, where the count is not scoped to one tenant
   * and adds up the same artefact across all of them.
   */
  const ownsNoWorklist = canSeeAllCompanies(me);
  /** Junk removal is company admin and above; the API enforces the same. */
  const canDelete = isCompanyAdmin(me);
  const [deleting, setDeleting] = useState<Call | null>(null);
  const canExport = isSupervisor(me);
  //: Upload is offered to whoever may log a call, matching the calls-only
  //: route's own gate. Bulk-logging is the same act as logging one row.
  const [importing, setImporting] = useState(false);
  /**
   * Whether the list names whose work each row is, and can be narrowed by it.
   *
   * The same predicate the Joining Tracker gates its recruiter filter on, and
   * deliberately so — two screens over the same people's work that disagreed
   * about who may see the names would be a rule nobody could state.
   */
  const monitors = seesOthersWork(me);
  /**
   * A recruiter reads the whole company's calls on this report.
   *
   * Only here, and only this report. The server widens /calls behind an
   * explicit company_wide flag and leaves subtree_user_ids alone, so
   * candidates, followups and interviews keep the scope they had — a
   * recruiter still cannot see a colleague's candidates.
   *
   * What they get is the outcome and whose call it was. Remarks on somebody
   * else's row are withheld server-side: they are candid notes about a
   * person, written by somebody who did not expect the company to read them.
   *
   * Monitors are untouched. They already have the mineOnly toggle over their
   * subtree, and widening them was not asked for.
   */
  const companyWide = !monitors;
  /**
   * Whether the table names whose call each row is.
   *
   * True for both readers of other people's rows now — a monitor across their
   * subtree, a recruiter across the company. Written as the disjunction rather
   * than as `true` so it stays honest if either half is ever narrowed.
   */
  const showsRecruiter = monitors || companyWide;

  /**
   * Every filter goes to the server, none of them to the loaded page.
   *
   * There are getting on for six thousand calls. Narrowing an array of fifty
   * would search the fifty that happened to arrive, which is not the same
   * question and quietly answers it wrong — a recruiter with no calls in the
   * newest fifty would come back empty rather than with their work.
   */
  /**
   * Where in the book we are.
   *
   * The list was fifty rows with no way past them and nothing saying so. On a
   * recruiter's own calls that was invisible; across a company of nearly six
   * thousand it is fifty of them presented as though it were all of them,
   * which is worse than an empty page because it looks complete.
   *
   * Reset to zero whenever a filter changes, below. Staying on page four of a
   * result that now has two pages would show an empty table for a filter that
   * matched plenty.
   */
  const [offset, setOffset] = useState(0);
  /**
   * Whose calls the report shows.
   *
   * The endpoint defaults mine_only to true and this page never overrode it,
   * so an owner — who logs no calls personally — read an empty report however
   * much the team had done. Now the people who monitor other people's work
   * ask for the company's calls and narrow to their own by choice.
   *
   * Off by default and a toggle rather than a hard switch, matching the "Only
   * mine" checkbox Interview Candidates has had all along. Consistency over a
   * second pattern: two screens onto the same call book should not disagree
   * about how you say "just mine".
   *
   * Offered to everyone now, recruiters included. That last clause used to say
   * a recruiter is held to their own rows by apply_owner_scope whatever this
   * is set to -- true on every other list, and never true here. This one
   * report deliberately widens a recruiter to the whole company (see
   * companyWide above), so they were the one reader looking at 35,596 calls
   * with no way to ask for their own.
   */
  const [mineOnly, setMineOnly] = useState(
    () => new URLSearchParams(window.location.search).get('mineOnly') === '1',
  );
  /**
   * Whether the shortlisted stay on the report.
   *
   * Off by default, which is what the client asked for: once somebody is
   * shortlisted they are worked from the Interview Tracker, and leaving the
   * row here is noise on a worklist.
   *
   * A filter and not a rule, which is the part worth keeping. Ticking this
   * brings them back, so "what did we say to them on the 3rd" has an answer
   * on the screen the call was logged on rather than only via the candidate.
   *
   * Narrower than excludePositive, which was the obvious reach and the wrong
   * one -- that also hides Interested, and the client's list keeps Interested
   * here as a Follow up.
   */
  const [showShortlisted, setShowShortlisted] = useState(false);
  /**
   * Whether to actually *ask* for the company, as opposed to being allowed to.
   *
   * companyWide is a fact about the reader; this is the request. They come
   * apart the moment a recruiter ticks "Only mine", because on the server
   * company_wide wins over mine_only -- deliberately, since the route defaults
   * mine_only to true and that default would otherwise cancel the widening.
   * The precedence is right for a default and wrong for a choice: without
   * this, ticking the box while the page asks for the company returns the
   * company, and the box does nothing at all.
   *
   * So the widening is dropped when somebody asks to narrow. The two flags are
   * opposite ends of one question -- how far past my own rows do I want to see
   * -- and sending both was the only way to phrase a contradiction.
   */
  const widenToCompany = companyWide && !mineOnly;
  const [recruiterId, setRecruiterId] = useState('');
  const [clientId, setClientId] = useState('');
  const [params] = useSearchParams();

  /**
   * Seeded from the URL, so a dashboard tile can open exactly what it counted.
   *
   * "Calls today" on My Day is a number, and a number you can click is a
   * promise that you will be shown those rows. It links here with the date
   * bounded to today and mineOnly set, which is precisely the population it
   * counted.
   *
   * Read once as an initial value, like `search` above and for the same
   * reason: the filters must not fight the address bar afterwards, in either
   * direction.
   */
  const [dateFrom, setDateFrom] = useState(() => params.get('dateFrom') ?? '');
  const [dateTo, setDateTo] = useState(() => params.get('dateTo') ?? '');
  /**
   * Seeded from ?search= so a link can land here already filtered.
   *
   * The delete dialog on Candidates names "1 call" as the blocker and links
   * to it by the candidate's number; without this the link would arrive at an
   * unfiltered page of six thousand rows, which is the hunting it exists to
   * avoid. Read once into state rather than driven by the URL, so typing in
   * the box afterwards does not fight the query string.
   */
  const [search, setSearch] = useState(() => params.get('search') ?? '');
  const [position, setPosition] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  // mineOnly counts as a filter, so "Clear filters" offers to undo it. It was
  // left out while the box was monitors-only and rarely the single thing set;
  // now that it is the one control a recruiter has here, a ticked box that
  // Clear leaves ticked is a page that says it cleared and did not.
  const filtered = Boolean(
    recruiterId || clientId || dateFrom || dateTo || search || position
    || outcomeFilter || mineOnly || showShortlisted,
  );
  // The box updates as it is typed; the request waits for a pause.
  const debouncedSearch = useDebounced(search);
  const positionTerm = useDebounced(position);

  /**
   * What the query actually searches for, which is normally the debounced box.
   *
   * The Search button and the Enter key publish the current text straight
   * away instead of waiting the pause out. Typing still searches on its own —
   * removing that would leave this one box needing a press while the six
   * filters beside it apply themselves, and somebody would type, see the old
   * rows, and report it as stale results.
   *
   * Held here rather than as a flush on useDebounced, because that hook is
   * shared with another screen and this is the only caller that wants it.
   */
  const [searchTerm, setSearchTerm] = useState(search);
  useEffect(() => { setSearchTerm(debouncedSearch); }, [debouncedSearch]);

  function runSearch() {
    setSearchTerm(search);
    setOffset(0);
  }

  /**
   * Setting a filter always returns to the first page.
   *
   * Wrapped once rather than remembered at four call sites — the one that got
   * forgotten would be the bug, and it would look like "the filter found
   * nothing" rather than "you are past the end".
   */
  function filterSetter(set: (value: string) => void) {
    return (value: string) => {
      set(value);
      setOffset(0);
    };
  }

  /**
   * The rows on screen, not the whole table.
   *
   * It used to send no filters at all, so somebody who had narrowed to one
   * client and one week downloaded every call in the company and had no way
   * to tell from the file. The same parameters the list query above sends,
   * read from the same state.
   */
  const exportCalls = useMutation({
    mutationFn: async () =>
      downloadExport({
        kind: 'calls',
        format: 'xlsx',
        params: {
          openOnly: true,
          mineOnly,
          ...(showShortlisted ? {} : { excludeShortlisted: true }),
          ...(widenToCompany ? { companyWide: true } : {}),
          ...(recruiterId ? { recruiterId } : {}),
          ...(clientId ? { clientId } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
          ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
          ...(positionTerm.trim() ? { position: positionTerm.trim() } : {}),
          ...(outcomeFilter ? { outcome: outcomeFilter } : {}),
        },
      }),
    onSuccess: (name) => toast.success(`Saved ${name}`),
    onError: (error) => {
      void exportErrorMessage(error).then((message) => toast.error(message));
    },
  });

  const [form, setForm] = useState({
    candidateName: '',
    candidatePhone: '',
    clientId: '',
    position: '',
    outcome: 'call_back' as CallOutcome,
    remarks: '',
  });

  /**
   * What the chosen client hires for, for the position field's suggestions.
   *
   * Their requirements first, then the positions already recorded against
   * their calls -- the second half is what makes this useful today, since far
   * fewer clients have a requirement raised than have calls logged.
   *
   * Only asked once a client is chosen: there is no such thing as the
   * positions of no client, and firing it on an empty id would be a request
   * whose answer could not be used.
   */
  const positions = useQuery<string[]>({
    queryKey: ['client-positions', form.clientId],
    queryFn: async () =>
      (await client.get(`/api/v1/clients/${form.clientId}/positions`)).data,
    enabled: Boolean(form.clientId),
    staleTime: 5 * 60_000,
  });

  /**
   * Whether somebody has already rung the number being typed.
   *
   * Asked while the number is entered, before the outcome is picked, because a
   * recruiter dials first and logs afterwards — a warning at submit time would
   * report a duplicate that already happened, which is a worse version of the
   * same problem.
   *
   * No debounce hook: the query key is the normalised number and the query is
   * enabled only once ten digits are present, so it fires once per complete
   * number rather than once per keystroke. React Query caches the answer, so
   * correcting a typo and coming back does not ask twice.
   */
  const phoneDigits = mobileDigits(form.candidatePhone);
  const { data: priorContact } = useQuery<PriorContact>({
    queryKey: ['calls', 'prior-contact', phoneDigits],
    enabled: Boolean(phoneDigits) && canLogCalls(me),
    // A colleague could log a call between two lookups, but a minute-old
    // answer is still the right one for a form being filled in now.
    staleTime: 60_000,
    queryFn: async () =>
      (
        await client.get('/api/v1/calls/prior-contact', {
          params: { phone: phoneDigits },
        })
      ).data,
  });

  /**
   * Somebody of this name already on file, under a different number.
   *
   * The other half of the duplicate question, and it is a different half.
   * prior-contact above asks "has this *number* been rung" and is answered
   * from the phone field; this asks "is this *person* already here" and is
   * answered from the name. A candidate who changed their mobile is invisible
   * to the first and caught by the second.
   *
   * Deliberately quieter than the phone warnings. Two people sharing a name is
   * ordinary, so this is a note rather than an alert, and it never blocks the
   * submit — which is the whole reason the endpoint warns instead of refusing.
   *
   * Debounced by the query key: the key is the trimmed, folded name, so
   * "Ravi" and "ravi " are one cache entry and the request fires once per
   * distinct name rather than once per keystroke. Floored at three characters,
   * below which the answer would be most of the book.
   */
  const typedName = form.candidateName.trim();
  const { data: possibleDuplicates } = useQuery<PossibleDuplicates>({
    queryKey: ['candidates', 'possible-duplicates', typedName.toLowerCase()],
    enabled: typedName.length >= 3 && canLogCalls(me),
    staleTime: 60_000,
    queryFn: async () =>
      (
        await client.get('/api/v1/candidates/possible-duplicates', {
          params: { name: typedName },
        })
      ).data,
  });

  /**
   * Has this candidate already gone to this client?
   *
   * The third warning, and the one the other two cannot give. Prior contact
   * asks whether the number has been rung; possible duplicates asks whether
   * the person is on file; this asks whether this person has already been put
   * to *this* client — which is the collision that wastes the client's
   * goodwill rather than the recruiter's afternoon.
   *
   * Needs both halves, so it waits for a complete number and a chosen client.
   * Closed attempts are included deliberately: an open one is already refused
   * a second time, and the case nothing caught was the candidate this client
   * rejected three weeks ago being sent again by somebody who did not know.
   */
  const { data: priorSubmissions } = useQuery<PriorSubmissions>({
    queryKey: ['submissions', 'prior', phoneDigits, form.clientId],
    enabled: Boolean(phoneDigits) && Boolean(form.clientId) && canLogCalls(me),
    staleTime: 60_000,
    queryFn: async () =>
      (
        await client.get('/api/v1/submissions/prior', {
          params: { phone: phoneDigits, clientId: form.clientId },
        })
      ).data,
  });
  const priorToThisClient = priorSubmissions?.submissions ?? [];

  /**
   * Shown only when the number has not already answered the question.
   *
   * A recruiter typing a known candidate's name *and* their known number does
   * not need two warnings about the same person -- the phone one is more
   * specific and fires first. This is for the case the phone cannot see.
   */
  const nameMatches = (possibleDuplicates?.matches ?? []).filter(
    (m) => m.candidateId !== priorContact?.candidateId,
  );

  //: Somebody else's call is the collision worth interrupting for. The
  //: caller's own earlier call is worth showing but is not a clash, so the two
  //: are separated here rather than in the markup.
  const clashes = priorContact?.calls.filter((c) => !c.isMine) ?? [];
  const ownPriorCall = priorContact?.calls.find((c) => c.isMine);
  const placed = priorContact?.placed ?? null;

  /** The same set the server filters on, rather than a second copy. */
  const positive = usePositiveOutcomes();

  /**
   * How many people are still waiting on a call.
   *
   * The caller's own, matching the table below — a company-wide number over a
   * personal list would count a different population than the rows under it.
   * Counted server-side through the same "most recent call" ordering the rest
   * of the page uses.
   */
  const { data: stillToCall } = useQuery<StillToCall>({
    queryKey: ['calls', 'still-to-call'],
    queryFn: async () =>
      (await client.get('/api/v1/calls/still-to-call')).data,
    // Not asked for at all by an owner. See the card below for why the number
    // is meaningless to them; not fetching it also keeps a cross-tenant count
    // from being computed for a screen that will not show it.
    enabled: !ownsNoWorklist,
  });

  /** The call waiting on a client before it can be tracked. */
  const [tracking, setTracking] = useState<Call | null>(null);
  /** The call being followed up, if any. */
  const [followingUp, setFollowingUp] = useState<Call | null>(null);
  const [editing, setEditing] = useState<Call | null>(null);
  /**
   * The server's refusal, kept beside the dialog rather than sent to a toast.
   *
   * An outcome on a superseded call and a re-point with a submission behind it
   * are both refused with a sentence naming what is in the way. That belongs
   * next to the field it is about, where somebody can act on it, not in a
   * toast that disappears while they are still reading the form.
   */
  const [editFailure, setEditFailure] = useState<string | null>(null);

  // The page no longer fetches clients itself. Every picker on it is a
  // ClientSelect, which asks /clients/options for the set its purpose needs --
  // so this screen also stops being subject to the limit: 200 that quietly
  // truncated the list once the importer had created several hundred names.
  //
  // See BACKLOG.md 20, which is the same cap in the recruiter dropdown.

  /**
   * The report is who still needs calling, not every call ever made.
   *
   * Each candidate's most recent call, and only where it left the question
   * open. Somebody who said busy on Monday and interested on Friday drops off
   * entirely rather than lingering on their old busy row and being rung a
   * third time — and this is the same ranking and the same settled set the
   * count above it uses, so the number and the rows describe one population.
   *
   * The tradeoff, stated: this is a worklist rather than a log. Three separate
   * attempts at one person show as one row, the newest.
   */
  const { data: calls, isLoading } = useQuery<Page<Call>>({
    // The acting company is not in the key — the request interceptor puts it
    // on the call, and switching company resets this cache at the picker.
    queryKey: [
      'calls',
      'report',
      mineOnly,
      showShortlisted,
      recruiterId,
      clientId,
      dateFrom,
      dateTo,
      searchTerm,
      positionTerm,
      outcomeFilter,
      offset,
    ],
    queryFn: async () =>
      (
        await client.get('/api/v1/calls', {
          params: {
            limit: PAGE_SIZE,
            offset,
            openOnly: true,
            // Sent explicitly rather than left to the server default, which is
            // true: an owner logs no calls personally and would read an empty
            // report. Everyone's toggle reaches this now; widenToCompany is
            // what keeps it from being cancelled on the way.
            mineOnly,
            ...(showShortlisted ? {} : { excludeShortlisted: true }),
            ...(widenToCompany ? { companyWide: true } : {}),
            ...(recruiterId ? { recruiterId } : {}),
            ...(clientId ? { clientId } : {}),
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            // Sent to the server, never applied to the loaded page: the table
            // is one page of ~6,000 rows, and filtering what has already
            // arrived would search fifty of them and call it a result.
            ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
            ...(positionTerm.trim() ? { position: positionTerm.trim() } : {}),
            ...(outcomeFilter ? { outcome: outcomeFilter } : {}),
          },
        })
      ).data,
  });

  const logCall = useMutation({
    mutationFn: async () =>
      (
        await client.post('/api/v1/calls', {
          candidateName: form.candidateName.trim(),
          candidatePhone: form.candidatePhone.trim(),
          clientId: form.clientId || null,
          outcome: form.outcome,
          position: form.position.trim() || null,
          remarks: form.remarks.trim() || null,
        })
      ).data,
    onSuccess: (created: Call) => {
      // Includes the still-to-call count, which this call may have just moved.
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      queryClient.invalidateQueries({ queryKey: ['my-day'] });
      if (created.matchedExistingCandidate) {
        // The number was already on file under a different name, so the call
        // was filed against that person. Correct if it is the same candidate
        // under a nickname; wrong if a digit was mistyped — and only the
        // recruiter can tell. Warning rather than success: it needs a look.
        toast.warning(
          `Logged against ${titleCaseNameOr(created.candidateName, 'that candidate')} — that mobile is already on file. `
          + 'Check it is the right person.',
          { duration: 8000 },
        );
      }
      // Three outcomes of one call, and the toast says which happened.
      // Shortlisting now creates the submission itself; before this the
      // recruiter had to press Track and had no way to tell whether the
      // tracker knew about the candidate.
      toast.success(
        created.submissionId
          ? 'Call logged — put forward to the client'
          : COMMITMENT_OUTCOMES.has(form.outcome)
            ? 'Call logged — followup scheduled'
            : 'Call logged',
      );
      setForm((f) => ({
        ...f,
        candidateName: '',
        candidatePhone: '',
        remarks: '',
      }));
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not log the call')),
  });

  /**
   * Log a second call to somebody already on this report.
   *
   * POST /calls, not PATCH: the outcome, date and remarks on a call are
   * deliberately not editable — see CallUpdate, which takes the client and
   * nothing else. Everything describing who was rung is carried from the
   * source call, so the two rows cannot disagree about the same candidate.
   */
  const followUp = useMutation({
    mutationFn: async ({
      call,
      outcome,
      remarks,
    }: {
      call: Call;
      outcome: CallOutcome;
      remarks: string;
    }) =>
      (
        await client.post('/api/v1/calls', {
          candidateId: call.candidateId,
          clientId: call.clientId,
          position: call.position,
          outcome,
          remarks: remarks.trim() || null,
        })
      ).data as Call,
    onSuccess: (created) => {
      setFollowingUp(null);
      // Prefix match: the list, the still-to-call count and the prior-contact
      // lookup all hang off ['calls'], and every one of them just moved.
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      // The Interested list is derived from each candidate's most recent
      // call, so this may have just added them to it — with nothing else to
      // press.
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // A follow-up call with a commitment outcome resolves the pending chase
      // and opens a new one, so the Recall list this may have been launched
      // from is now wrong in both directions.
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      toast.success(
        positive.has(created.outcome)
          ? 'Logged — they are now on Interview Candidates'
          : 'Follow-up call logged',
      );
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Submit the candidate from a call, so they appear on the Tracker.
   *
   * The server reads the candidate, position, date and owning recruiter off
   * the call; clientId is only sent when the call has none. A second press
   * comes back with alreadyTracked, having created nothing.
   */
  const track = useMutation({
    mutationFn: async ({ call, clientId }: { call: Call; clientId?: string }) =>
      (
        await client.post(
          `/api/v1/calls/${call.id}/submit`,
          clientId ? { clientId } : {},
        )
      ).data as CallSubmitResult,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['tracker'] });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
      setTracking(null);
      toast.success(
        result.alreadyTracked
          ? `${titleCaseNameOr(result.candidateName, 'That candidate')} is already on the Tracker for ${result.clientName}`
          : `${titleCaseNameOr(result.candidateName, 'Candidate')} added to the Tracker for ${result.clientName}`,
      );
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not track this candidate')),
  });

  /**
   * Correcting a logged call.
   *
   * The body is only what the dialog saw change — see EditCallDialog. The
   * server reads what was set rather than what was sent, so anything omitted
   * here is left exactly as it was.
   */
  const edit = useMutation({
    mutationFn: async ({ call, changes }: { call: Call; changes: Record<string, unknown> }) =>
      (await client.patch(`/api/v1/calls/${call.id}`, changes)).data as Call,
    onSuccess: () => {
      // The chase may have been rescheduled or closed, and the candidate may
      // have moved on or off the Interested list — so this reaches further
      // than the row that was edited.
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['still-to-call'] });
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      // The dashboard reads the same rows under Recall, so a correction made
      // there has to reach the list it was made from. Without this the row
      // sat unchanged until a reload -- the screen showing the stale copy
      // being the one the edit was started on.
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setEditing(null);
      setEditFailure(null);
      toast.success('Call corrected');
    },
    onError: (e) => setEditFailure(errorMessage(e, 'Could not correct that call')),
  });

  /**
   * Take over follow-up on a call whose owner cannot sign in.
   *
   * Its own route rather than part of the edit, because it moves work between
   * people rather than correcting what was recorded -- and it is audited as
   * call.taken with the owner before and after.
   *
   * Deliberately does not touch the candidate. A recruiter taking one call has
   * not looked at that person's whole history; claiming them is the other
   * control, on the Candidates page, and theirs to choose.
   */
  const take = useMutation({
    mutationFn: async (callId: string) =>
      (await client.post(`/api/v1/calls/${callId}/take`)).data as Call,
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setEditing(updated);
      toast.success('This call is yours now');
    },
    onError: (error) =>
      toast.error(errorMessage(error, 'Could not take that call')),
  });

  const createsFollowup = COMMITMENT_OUTCOMES.has(form.outcome);

  const waiting = stillToCall?.count ?? 0;

  return (
    <div className="space-y-6">
      {/* Above the form, because it is the reason to open the page rather than
          a summary of what is on it. Hidden at zero: a large "0 still to call"
          is a scoreboard, and the useful thing about this number is that it
          asks for something. */}
      {!ownsNoWorklist && waiting > 0 && (
        <section className="flex items-center gap-4 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <p className="text-4xl font-semibold tabular-nums text-amber-900">
            {waiting.toLocaleString('en-IN')}
          </p>
          <div>
            <p className="text-sm font-medium text-amber-900">
              {waiting === 1 ? 'candidate to call back' : 'candidates to call back'}
            </p>
            {/* Says what it counts, because "still to call" could reasonably
                mean several things and a number nobody can reproduce gets
                argued with rather than worked. */}
            <p className="text-xs text-amber-800">
              Their last call ended without an answer either way — busy, no
              reply, call back, and the rest. Interested and not interested are
              settled and not counted.
            </p>
          </div>
        </section>
      )}

      {/* Not offered to the owner, who is here to read what the team did
          rather than to add to it. A visibility rule only: POST /calls still
          accepts any staff member, the owner included. */}
      {canLogCalls(me) && (
        <section className="card border-2 border-indigo-100 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
            <PhoneCall className="h-4 w-4 text-indigo-600" />
            Log a Call
          </h2>

          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!form.candidateName.trim()) {
                toast.error('Candidate name is required');
                return;
              }
              // Checked here as well as on the server, which refuses it too.
              // A candidate with no number cannot be rung, and cannot be
              // matched to the person who calls next — get_or_create_candidate
              // dedupes on the mobile and nothing else, so a numberless row is
              // a fresh person every time the same name is typed.
              if (!mobileDigits(form.candidatePhone)) {
                toast.error(
                  form.candidatePhone.trim()
                    ? 'That mobile number is not 10 digits'
                    : 'Mobile number is required',
                );
                return;
              }
              // The server refuses this too, with the date of the call it
              // clashes with. Stopped here as well so the answer arrives
              // before the round trip rather than after it — and only when the
              // lookup has actually returned, so a slow answer never silently
              // lets one through: the 409 is the backstop.
              if (priorContact?.loggedToday) {
                toast.error('This candidate already has a call logged today');
                return;
              }
              logCall.mutate();
            }}
          >
            <div>
              <label className="label" htmlFor="name">Candidate Name</label>
              <input
                spellCheck={false}
                id="name"
                className="input"
                value={form.candidateName}
                onChange={(e) => setForm({ ...form, candidateName: e.target.value })}
                aria-describedby={nameMatches.length ? 'name-duplicates' : undefined}
                placeholder="Full Name e.g. Abhi Lakke"
                required
              />

              {/* Somebody of this name is already on file under another number.
                A note, not an alert: two people sharing a name is ordinary,
                and the mobile — which is the unique one — has its own louder
                warnings above. Never blocks the submit, which is the whole
                reason the endpoint warns rather than refusing.

                Named rather than counted. "1 possible duplicate" tells a
                recruiter nothing they can act on; "Ravi Kumar …3210, Priya's"
                is the sentence that lets them recognise their own entry or
                spot a colleague's. */}
              {nameMatches.length > 0 && (
                <p
                  id="name-duplicates"
                  role="status"
                  className="mt-1 text-[11px] leading-relaxed text-amber-700"
                >
                  Already on file:{' '}
                  {nameMatches.slice(0, 3).map((m, i) => (
                    <span key={m.candidateId}>
                      {i > 0 && '; '}
                      {titleCaseName(m.fullName)}
                      {m.phoneHint && ` ${m.phoneHint}`}
                      {!m.isMine && ' (a colleague’s)'}
                    </span>
                  ))}
                  {nameMatches.length > 3 && ` and ${nameMatches.length - 3} more`}
                  . A different number makes a different record.
                </p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="phone">
                Mobile <span className="text-red-600">*</span>
              </label>
              <input
                spellCheck={false}
                id="phone"
                className="input"
                inputMode="numeric"
                placeholder="10 digits"
                maxLength={10}
                value={form.candidatePhone}
                required
                /* Ten digits, and anything else is dropped as it is typed. A
                   field that takes "+91 98765 43210" and a field that takes
                   "9876543210" describe the same number, and accepting both
                   means the server silently corrects something the person is
                   looking straight at. */
                onChange={(e) =>
                  setForm({
                    ...form,
                    candidatePhone: sanitiseMobileInput(e.target.value),
                  })}
                aria-describedby={
                  placed
                    ? 'placed-warning'
                    : clashes.length
                      ? 'prior-contact'
                      : undefined
                }
              />

              {/* Already in the call log, so the write will be refused.

                Red and first, above the placed and prior-contact warnings,
                because those two are judgements the recruiter gets to make and
                this one is not: the server answers 409 whatever they decide.
                A warning that looks like the others would read as "consider
                this" when it means "this will not save".

                Reads ever_logged, not `total`. That counts inside the 30-day
                window and the rule has no window in it — a number last rung a
                year ago would show nothing here and then fail on submit. */}
              {priorContact?.loggedToday && (
                <p
                  role="alert"
                  className="mt-1.5 rounded border border-red-300 bg-red-50 px-2.5 py-2 text-xs text-red-900"
                >
                  <span className="font-medium">
                    This candidate already has a call logged today.
                  </span>{' '}
                  Edit that row to change what happened, or log the next call
                  on another day.
                </p>
              )}

              {/* This number belongs to somebody we placed, who is still in the
                job. Above the duplicate warning and louder than it, because
                the two say different things: a duplicate may still be a
                legitimate call and only the recruiter knows, while this is our
                own client's employee being rung about another role.

                role="alert" rather than "status" for the severe version — a
                fee already received, or a guarantee still running — so a
                screen reader interrupts rather than waiting to be asked.

                Still does not block the submit. A rehire is real, and so is a
                second role at the same group; the recruiter is the one who
                knows which this is. See lib/placedWarning for why the warning
                fires on the placement and not on the payment. */}
              {placed && (() => {
                const copy = placedWarningCopy(placed);
                return (
                  <div
                    id="placed-warning"
                    role={copy.severe ? 'alert' : 'status'}
                    className={`mt-1.5 rounded border px-2.5 py-2 text-xs ${copy.severe
                        ? 'border-red-300 bg-red-50 text-red-900'
                        : 'border-amber-300 bg-amber-50 text-amber-900'
                      }`}
                  >
                    <p className="font-medium">{copy.headline}</p>
                    {copy.detail && <p className="mt-0.5">{copy.detail}</p>}
                  </div>
                );
              })()}

              {/* Somebody else has rung this number lately. Amber and inline
                rather than a toast: a toast is for something that has already
                happened, and this is a decision the recruiter is still in the
                middle of making. It never blocks the submit — the call may
                well be legitimate, and only the recruiter knows. */}
              {clashes.length > 0 && (
                <div
                  id="prior-contact"
                  role="status"
                  className="mt-1.5 rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900"
                >
                  <p className="font-medium">
                    Already called
                    {priorContact?.candidateName
                      ? ` — on file as ${titleCaseNameOr(priorContact.candidateName, 'someone else')}`
                      : ''}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {clashes.map((c) => (
                      <li key={`${c.recruiterUserId}-${c.callDate}`}>
                        {titleCaseName(c.recruiterName) ?? 'A former colleague'} ·{' '}
                        {whenPhrase(c.callDate)} ·{' '}
                        {OUTCOME_LABELS[c.outcome] ?? c.outcome}
                      </li>
                    ))}
                  </ul>
                  {priorContact && priorContact.total > priorContact.calls.length && (
                    <p className="mt-1 text-amber-700">
                      {priorContact.total - priorContact.calls.length} more in the
                      last {priorContact.windowDays} days.
                    </p>
                  )}
                </div>
              )}

              {/* The recruiter's own earlier call. Worth surfacing — they may
                simply have forgotten — but it is not two people colliding, so
                it does not get the amber treatment. */}
              {clashes.length === 0 && ownPriorCall && (
                <p className="mt-1.5 text-xs text-gray-500">
                  You called this number {whenPhrase(ownPriorCall.callDate)} —{' '}
                  {OUTCOME_LABELS[ownPriorCall.outcome] ?? ownPriorCall.outcome}.
                </p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="client">Client Name</label>
              {/* Logging a new call picks from the same set: every client. The
                argument for leaving the imported ones out was that recording a
                call against a spelling added a fourth row to a company that
                already had three. Those three are one row now, so the cost has
                inverted -- the call would have nowhere to go. */}
              <ClientSelect
                purpose="work"
                id="client"
                value={form.clientId}
                onChange={(clientId) =>
                  // The position belongs to the client. Keeping a role typed
                  // for one company against another would file the call under a
                  // job that client is not hiring for, so changing the client
                  // clears it rather than carrying it across.
                  setForm({ ...form, clientId, position: '' })
                }
                placeholder="Select Client"
              />

              {/* Already put to this client, lately.

                The collision that costs the client's goodwill rather than the
                recruiter's afternoon — and the one the phone warnings above
                cannot see, since they know nothing about which client was
                chosen. Amber because it is somebody else's client relationship
                at stake; still never blocks the submit, because a rejection in
                March and a fresh attempt in September is a real second go.

                Closed attempts included on purpose: an open one is already
                refused a second time by the server. */}
              {priorToThisClient.length > 0 && (
                <p
                  role="status"
                  className="mt-1 text-[11px] leading-relaxed text-amber-700"
                >
                  Already put to this client:{' '}
                  {priorToThisClient.slice(0, 2).map((s, i) => (
                    <span key={s.submissionId}>
                      {i > 0 && '; '}
                      {s.submittedOn}
                      {s.appliedPosition && ` for ${s.appliedPosition}`}
                      {` — ${s.status.replace(/_/g, ' ')}`}
                      {s.isOpen && ' (still open)'}
                      {!s.isMine && s.recruiterName && ` by ${titleCaseName(s.recruiterName)}`}
                    </span>
                  ))}
                  {priorToThisClient.length > 2
                    && ` and ${priorToThisClient.length - 2} more`}
                  {` in the last ${priorSubmissions?.windowDays ?? 90} days.`}
                </p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="position">Position Name</label>
              {/* Suggestions, not a closed list.

                The options are the roles this client is actually hiring for --
                their requirements first, then the positions already recorded
                against their calls -- which is what "interconnected" asks for:
                pick the client and the field knows what they hire.

                It stays typable on purpose. Production has 106 clients and far
                fewer raised requirements, so a closed list would be empty for
                most of them, and a client hiring for something nobody has
                logged yet must not be unrecordable because the endpoint has
                not heard of it. A datalist gives both: the list when it helps,
                a text field when it does not.

                Disabled until a client is chosen, because until then there is
                nobody whose positions these would be. */}
              <input
                id="position"
                className="input"
                list="client-positions"
                value={form.position}
                maxLength={160}
                disabled={!form.clientId}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                placeholder={
                  form.clientId ? 'Start typing, or pick one' : 'Choose a client first'
                }
              />
              <datalist id="client-positions">
                {(positions.data ?? []).map((title) => (
                  <option key={title} value={title} />
                ))}
              </datalist>
            </div>

            <div>
              {/* "Recruiter Remarks", and the inconsistency it used to have
                with the column of the same meaning is gone. This said
                Outcome because a free-text Remarks box sat directly beside
                it, and two adjacent controls sharing a label would have
                been worse. That box was removed with the new outcome list,
                so the reason for the difference went with it. */}
              <label className="label" htmlFor="outcome">Recruiter Remarks</label>
              <select
                id="outcome"
                className="input"
                value={form.outcome}
                onChange={(e) =>
                  setForm({ ...form, outcome: e.target.value as CallOutcome })
                }
              >
                {OUTCOMES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {createsFollowup && (
                <p className="mt-1 text-xs text-indigo-600">
                  A followup will be scheduled automatically.
                </p>
              )}
            </div>

            {/* In the grid's own last cell rather than on a row of its own:
              the second column ends at Position Name, so the button fills the
              gap beside Recruiter Remarks instead of pushing the card taller
              by an empty row. Right-aligned still, which is where the submit
              sits on every dialog in the application.

              `items-end` rather than centred, because the field beside it
              grows a line when the outcome schedules a followup -- the button
              stays level with the bottom of the select instead of drifting
              down the cell with that hint. */}
            <div className="flex items-end justify-end">
              <button
                type="submit"
                className="btn-primary"
                disabled={logCall.isPending || Boolean(priorContact?.loggedToday)}
              >
                {logCall.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Add Call
              </button>
            </div>
          </form>
        </section>
      )}

      {/* The tracker's filter bar, not a second pattern: same label-wrapping
          a select, same "Everyone"/"Any" empty option, same Clear that only
          appears once something is set. Two screens onto the same work should
          not need learning twice.

          Shown to everyone now, where it used to be supervisors only. A
          recruiter reading their own six months of calls has the same problem
          a manager reading the company's does, and every control here except
          the two below narrows a list they can already see. */}
      <section className="card border-2 border-indigo-100 shadow-sm [&_label]:text-sm [&_label]:font-semibold [&_label]:text-gray-700">
        {/* Two rows rather than one wrapping row. Left to wrap, From landed
              at the end of the first line and To at the start of the second,
              so the pair that only means anything read together was split
              across the break. Naming the rows puts the dates side by side at
              any width. */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            {monitors && (
              <RecruiterFilter
                className="w-44"
                // The size every other field on this bar runs at. Left to its
                // own default it kept the compact sizing the rest of the bar
                // used to have, so it sat shorter than its neighbours and its
                // label rode a half-step above theirs.
                selectClassName="input mt-1"
                value={recruiterId}
                onChange={filterSetter(setRecruiterId)}
              />
            )}

            {/* Input and button in their own flex box so they wrap together.
                As siblings of the row they would separate on a narrow screen,
                leaving a Search button under a different filter. */}
            <div className="flex items-end gap-2">
              <label className="w-64 text-xs text-gray-600">
                Candidate
                <input
                  type="search"
                  className="input mt-1"
                  placeholder="Name or Mobile Number"
                  value={search}
                  onChange={(e) => filterSetter(setSearch)(e.target.value)}
                  // Enter searches now rather than after the pause. A plain
                  // input outside a form swallows it otherwise, which reads
                  // as the key doing nothing.
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runSearch();
                    }
                  }}
                />
              </label>
              {/* No size overrides: btn and input share px-3/py-2 and text-sm,
                  so items-end lines them up without a nudge.

                  It carried a spinner off isFetching and disabled itself while
                  one was in flight. Both are gone. isFetching stays true
                  through React Query's retries, so a request that was failing
                  left a permanently spinning button nobody could press -- a
                  control that looks broken and then is. The table below has
                  the loading state; a filter button does not need a second
                  one. */}
              <button
                type="button"
                className="btn-primary"
                onClick={runSearch}
              >
                <Search className="h-4 w-4" />
                Search
              </button>
            </div>

            <label className="w-44 text-xs text-gray-600">
              Client Name
              <ClientSelect
                purpose="work"
                className="input mt-1"
                value={clientId}
                onChange={filterSetter(setClientId)}
                placeholder="Any Client"
              // Every client, imported ones included. This used to leave
              // them out on the grounds that several hundred spreadsheet
              // names would bury the ones anybody typed in -- true of the
              // 998-client development database it was written against, and
              // not of the 106 in production. What it cost was worse than
              // what it saved: the calls on this page are overwhelmingly
              // against imported clients, so the filter could not name the
              // rows it was filtering.
              />
            </label>

            <label className="w-44 text-xs text-gray-600">
              Position Name
              <input
                type="search"
                className="input mt-1"
                placeholder="Any Position"
                value={position}
                onChange={(e) => filterSetter(setPosition)(e.target.value)}
              />
            </label>

            <label className="w-44 text-xs text-gray-600">
              Recruiter Remarks
              <select
                className="input mt-1"
                value={outcomeFilter}
                onChange={(e) => filterSetter(setOutcomeFilter)(e.target.value)}
              >
                {/* "Any remark", like the two filters beside it. It repeated
                    its own label before, which read as a leftover once the
                    label stopped being the same word. */}
                <option value="">Any Remark</option>
                {/* The filter offers the retired outcomes too. A recruiter
                    looking for the Interested calls they logged last month
                    still has to be able to find them. */}
                {OUTCOME_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            {/* A real date input, so the browser supplies its own calendar
                and its own keyboard entry.

                It was free text with a DD-MM-YYYY mask, which bought one
                thing -- a format that matched the sheets -- and cost the
                picker. The value here is ISO either way: `type="date"` holds
                yyyy-mm-dd, which is exactly what the API wants, so the
                parsing this replaces was converting a format into the format
                it started from.

                What the *display* looks like is the browser's, from its
                locale: dd-mm-yyyy on a browser set to English (India),
                mm/dd/yyyy on English (United States). No attribute can
                override it. That is worth knowing rather than hiding -- see
                the commit -- because 01-02-2026 means different days under the
                two, and it is the same ambiguity parse_indian_date already
                fights in the importer. */}
            <label className="w-44 text-xs text-gray-600">
              From
              <input
                type="date"
                className="input mt-1"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => filterSetter(setDateFrom)(e.target.value)}
              />
            </label>

            <label className="w-44 text-xs text-gray-600">
              To
              <input
                type="date"
                className="input mt-1"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => filterSetter(setDateTo)(e.target.value)}
              />
            </label>

            {/* Everyone, recruiters included. It was monitors-only on the
                reasoning that apply_owner_scope pins a recruiter to their own
                rows regardless -- which holds on every other list and never
                held here, because this report widens them to the whole
                company on purpose. They were the one reader who could not ask
                for their own calls. */}
            <label className="flex items-center gap-2 pb-2 text-xs text-gray-600">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={showShortlisted}
                onChange={(e) => {
                  setShowShortlisted(e.target.checked);
                  setOffset(0);
                }}
              />
              Show moved to Interview Candidates
            </label>

            <label className="flex items-center gap-2 pb-2 text-xs text-gray-600">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={mineOnly}
                onChange={(e) => {
                  setMineOnly(e.target.checked);
                  setOffset(0);
                }}
              />
              Only mine
            </label>

            {filtered && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setRecruiterId('');
                  setClientId('');
                  setDateFrom('');
                  setDateTo('');
                  setSearch('');
                  setPosition('');
                  setOutcomeFilter('');
                  setMineOnly(false);
                  setShowShortlisted(false);
                  setOffset(0);
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* The count below is the filtered total, not the book. Worth
              saying, because "1,204 calls" under a recruiter filter is that
              recruiter's number and reads like the company's. */}
        {filtered && (
          <p className="mt-2 text-xs text-gray-400">
            The count below is what matches these filters, not every call on
            file.
          </p>
        )}
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">
            Recent calls{' '}
            {calls && (
              /* Which rows these are, not just how many exist. "50" beside a
                 table of fifty says nothing; "1-50 of 5,993" is the sentence
                 that was missing. */
              <span className="badge bg-gray-100 text-gray-600">
                {calls.total === 0
                  ? 'none'
                  : `${(offset + 1).toLocaleString('en-IN')}–${Math.min(
                    offset + calls.items.length,
                    calls.total,
                  ).toLocaleString('en-IN')} of ${calls.total.toLocaleString('en-IN')}`}
              </span>
            )}
          </h2>
          {/* Exports are approver-only server-side; showing a recruiter a
              button that can only 403 is worse than not showing it. */}
          <div className="flex items-center gap-1">
            {canBulkImportCalls(me) && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setImporting(true)}
                title="Bulk-log Calls From a Spreadsheet"
              >
                <Upload className="h-3.5 w-3.5" />
                Import
              </button>
            )}
            {canExport && (
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={exportCalls.isPending}
                onClick={() => exportCalls.mutate()}
                title="Colour-coded by outcome, like the old tracker sheet"
              >
                {exportCalls.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-3.5 w-3.5" />
                )}
                Excel
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <CallsTable
            rows={calls?.items ?? []}
            showsRecruiter={showsRecruiter}
            busy={track.isPending || followUp.isPending}
            onTrack={(row) => {
              // A call that already names a client needs no dialog; one
              // that does not has to be asked which, because that is the
              // only thing a submission needs that a call may not have.
              if (row.clientId) track.mutate({ call: row });
              else setTracking(row);
            }}
            onFollowUp={setFollowingUp}
            onEdit={(row) => {
              setEditFailure(null);
              setEditing(row);
            }}
            onDelete={canDelete ? setDeleting : undefined}
          />
        )}

        {/* Offered whenever there is more than one page, including on the
            last one — a Previous that vanishes at the end strands whoever
            paged there. Both buttons disable at their edge rather than
            disappearing, so the control does not move under the cursor. */}
        {calls && calls.total > PAGE_SIZE && (
          <div className="mt-3 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Page {Math.floor(offset / PAGE_SIZE) + 1} of{' '}
              {Math.ceil(calls.total / PAGE_SIZE).toLocaleString('en-IN')}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Previous
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={offset + PAGE_SIZE >= calls.total}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      {editing && (
        <EditCallDialog
          key={editing.id}
          call={editing}
          outcomes={OUTCOMES}
          saving={edit.isPending}
          failure={editFailure}
          taking={take.isPending}
          onTake={() => take.mutate(editing.id)}
          onCancel={() => {
            setEditing(null);
            setEditFailure(null);
          }}
          onSave={(changes) => edit.mutate({ call: editing, changes })}
        />
      )}

      {deleting && (
        <DeleteRecordDialog
          key={deleting.id}
          noun="call"
          checkUrl={`/api/v1/calls/${deleting.id}/delete-check`}
          deleteUrl={`/api/v1/calls/${deleting.id}`}
          /* A follow-up exists because of a call. With the call gone it is a
             reminder to chase a conversation that is no longer recorded, so
             it goes too rather than surviving as an orphan on somebody's
             Recall list. */
          cascade
          /* A follow-up is the only thing that can block a call, and there is
             no follow-up delete — so it is named and counted with no link,
             which is the honest answer. */
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void queryClient.invalidateQueries({ queryKey: ['calls'] });
          }}
        />
      )}

      {tracking && (
        <TrackDialog
          // Keyed by call, so the choice belongs to the row it was opened on.
          key={tracking.id}
          call={tracking}
          submitting={track.isPending}
          onCancel={() => setTracking(null)}
          onConfirm={(clientId) => track.mutate({ call: tracking, clientId })}
        />
      )}

      {followingUp && (
        <FollowUpDialog
          // Keyed by call, so the outcome belongs to the row it was opened on
          // rather than carrying over from the last one.
          key={followingUp.id}
          call={followingUp}
          outcomes={OUTCOMES}
          submitting={followUp.isPending}
          onCancel={() => setFollowingUp(null)}
          onConfirm={(outcome, remarks) =>
            followUp.mutate({ call: followingUp, outcome, remarks })
          }
        />
      )}

      {/* The calls-only route, not the full importer. A recruiter may
          bulk-log their calls; authoring submissions and placements in bulk
          is a different act and stays with a company admin. */}
      {importing && (
        <ImportPanel
          endpoint="/api/v1/imports/calls"
          /* Sliced, because this is the sheet that got large. A 62,728-row
             workbook asked for one eight-and-a-half-minute request and the
             connection did not last; the same rows arrive in pieces. */
          stageEndpoint="/api/v1/imports/calls/stage"
          title="Import Calls From Excel"
          onClose={() => setImporting(false)}
        />
      )}
    </div>
  );
}
